import React, { useCallback, useRef, useState } from 'react';
import { Camera, X, Loader2, Maximize2 } from 'lucide-react';
import { shrinkImage } from '../../lib/imageResize';
import PhotoLightbox from './PhotoLightbox';

export const MAX_PHOTOS = 6;

// The shots we need to confirm fit before we book — the frame is the one people forget,
// and it is the one that decides whether the latch and strike will line up.
export const PHOTO_SHOTS = [
  { n: 1, title: 'Door edge, tape from the edge to the lock centre', text: 'That distance is the backset.', img: '/images/work/narrow-mortice-aluminium-door.jpeg' },
  { n: 2, title: 'Inside of the door, shut', text: 'The whole lock with the frame beside it.', img: '/images/work/digital-lock-inside.jpeg' },
  { n: 3, title: 'The frame where it latches', text: 'The strike plate or keeper — it decides the fit.', img: '/images/work/strike-lock-wooden-frame.jpeg' },
  { n: 4, title: 'Outside, stepped back', text: 'Whole door, handle height, how sheltered it is.', img: null },
];
// Checkout wants the three that decide fit first; the frame shot is welcome but not a blocker.
export const CHECKOUT_SHOTS = [PHOTO_SHOTS[0], PHOTO_SHOTS[1], PHOTO_SHOTS[3], { ...PHOTO_SHOTS[2], title: 'The frame where it latches (if you can)' }].map((s, i) => ({ ...s, n: i + 1 }));

// Photo picker for the enquiry forms: phone photos are shrunk in the browser, shown as
// thumbnails, and handed up as base64 JPEGs. `photos` / `onChange` are owned by the form.
export default function DoorPhotos({ photos, onChange, testId = 'door-photos', showGuide = true, shots = PHOTO_SHOTS }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Example shots open full size, like the install guide photos.
  const examples = shots.filter((sh) => sh.img);
  const [open, setOpen] = useState(null);
  const close = useCallback(() => setOpen(null), []);
  const move = useCallback((d) => setOpen((i) => (i === null ? null : Math.min(examples.length - 1, Math.max(0, i + d)))), [examples.length]);

  const add = async (files) => {
    setError('');
    const room = MAX_PHOTOS - photos.length;
    const list = Array.from(files || []).slice(0, Math.max(room, 0));
    if (Array.from(files || []).length > room) setError(`Up to ${MAX_PHOTOS} photos — the first ${room} were added.`);
    if (!list.length) return;
    setBusy(true);
    const next = photos.slice();
    const failed = [];
    for (const file of list) {
      try {
        next.push(await shrinkImage(file));
      } catch {
        failed.push(file.name || 'photo');
      }
    }
    setBusy(false);
    onChange(next);
    if (failed.length) setError(`Could not read ${failed.join(', ')} — a JPEG or a screenshot of it works.`);
  };
  const remove = (i) => onChange(photos.filter((_, j) => j !== i));

  return (
    <div data-testid={testId}>
      {showGuide && (
        <ol className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid={`${testId}-guide`}>
          {shots.map((s) => (
            <li key={s.n} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              {s.img ? (
                <button type="button" onClick={() => setOpen(examples.indexOf(s))} className="group relative block w-full bg-gray-100 text-left" aria-label={`Enlarge example: ${s.title}`}>
                  <img src={s.img} alt={s.title} className="w-full aspect-[3/4] object-cover" loading="lazy" />
                  <span className="absolute top-2 left-2 inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-900 text-white text-sm font-bold shadow">{s.n}</span>
                  <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-xs px-2 py-1 group-hover:bg-black/80"><Maximize2 size={12} /> Enlarge</span>
                </button>
              ) : (
                <div className="relative w-full aspect-[3/4] bg-gray-100 flex items-center justify-center text-gray-400">
                  <Camera size={36} />
                  <span className="absolute top-2 left-2 inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-900 text-white text-sm font-bold shadow">{s.n}</span>
                </div>
              )}
              <div className="p-3">
                <p className="font-semibold text-gray-900 text-sm leading-snug">{s.title}</p>
                <p className="text-xs text-gray-600 mt-1">{s.text}</p>
              </div>
            </li>
          ))}
        </ol>
      )}
      {open !== null && <PhotoLightbox items={examples.map((e) => ({ src: e.img, title: e.title, text: e.text }))} index={open} onClose={close} onMove={move} label="Shot" testId={`${testId}-lightbox`} />}
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ''; }} data-testid={`${testId}-input`} />
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => inputRef.current && inputRef.current.click()} disabled={busy || photos.length >= MAX_PHOTOS} className="inline-flex items-center gap-2 rounded-full border-2 border-gray-900 px-4 py-2 font-semibold text-gray-900 hover:bg-gray-900 hover:text-white disabled:opacity-50">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <Camera size={18} />}
          {photos.length ? 'Add another photo' : 'Add photos of the door'}
        </button>
        <span className="text-sm text-gray-600">{photos.length} of {MAX_PHOTOS} · phone photos are fine, we shrink them</span>
      </div>
      {photos.length > 0 && (
        <ul className="flex flex-wrap gap-3 mt-3" data-testid={`${testId}-list`}>
          {photos.map((p, i) => (
            <li key={`${p.name}-${i}`} className="relative">
              <img src={p.preview} alt={p.name} className="w-24 h-24 object-cover rounded-lg border border-gray-200" />
              <button type="button" onClick={() => remove(i)} className="absolute -top-2 -right-2 rounded-full bg-gray-900 text-white p-1 shadow" aria-label={`Remove ${p.name}`}>
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="text-sm text-red-700 mt-2">{error}</p>}
    </div>
  );
}
